import React, { type CSSProperties, type FC, type ReactElement } from 'react'
import moment, { type Moment } from 'moment'
import { Bar, Line } from 'react-chartjs-2'
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Tab,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip
} from '@mui/material'

import {
  ColoredLabel,
  ColoredValue,
  GraphIconButton,
  StyledAppBar,
  StyledInput,
  StyledTableCell,
  TableHeadCellBordered,
  useTabStyles
} from './MetersTableRow.style'
import SettingsIcon from '@mui/icons-material/Settings'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import BarChart from '@mui/icons-material/BarChart'

import { MetersTableLabelsField } from '../MetersTableLabelsField'
import { StyledLabel } from '../../meter-configuration/MeterConfiguration.style'
import { type Meter, type MeterDataScale } from '../../../../redux/meters/meter-types'
import { meterSupportsAlarm } from '../../../../utils/meter-utils'
import { useTranslation } from 'react-i18next'
import { Chart, registerables } from 'chart.js'
import { isTimestampOverDayOld } from '../../../../utils/utils'
import { FLOW_LIMIT, getAlarmTypeByKey, isAlarmKey } from '../../../../redux/alarm-settings/alarm-types'
import { AlarmsDetails, AlarmStatusIcon } from './AlarmsDetails'
import { type Alarm } from './types'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { type RootState } from '../../../../redux/store'
import { DeviceType } from '../../../../api/device-api-types'
import { FetchMeterHistoryValuesAction } from '../../../../redux/meters/meter-actions'

Chart.register(...registerables)

const graphOptions = {
  scales: {
    x: {
      grid: {
        display: false
      }
    }
  },
  maintainAspectRatio: false,
  responsive: true,
  plugins: {
    tooltip: {
      intersect: false
    },
    legend: {
      display: false
    }
  },
  data: {
    datasets: {
      line: {
        radius: 1
      }
    }
  }
}

interface GraphProps {
  scale: MeterDataScale
  startDate: moment.Moment
  endDate: moment.Moment
}

interface Props {
  meter: Meter
  logo: string
  dateAndTimeUpdating: string | null
  dateAndTimeCoupling: string
  updatedValue: string
  idVisibility?: CSSProperties
  usageVisibility?: CSSProperties
  isPulse: boolean
  labelVisibility?: React.CSSProperties
  onSettingsIconClick: () => void
  onLabelExpandClick: () => void
}

/**
 * Meter table row component.
 */
export const MetersTableRow: FC<Props> = ({
  meter,
  logo,
  onLabelExpandClick,
  onSettingsIconClick,
  dateAndTimeUpdating,
  dateAndTimeCoupling,
  updatedValue,
  idVisibility,
  usageVisibility,
  isPulse,
  labelVisibility
}) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const [expandedMeterId, setExpandedMeterId] = React.useState<string | null>(null)
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false)

  const [graphStartDate, setGraphStartDate] = React.useState<moment.Moment>(
    moment().startOf('month')
  )
  const [graphEndDate, setGraphEndDate] = React.useState<moment.Moment>(
    moment().endOf('month')
  )
  const [graphScale, setGraphScale] = React.useState<MeterDataScale>('day')

  const isMeterOutdated = isTimestampOverDayOld(meter.updatedAt)
  const alarms: Alarm[] = calculateAlarms(meter)

  const graphProps: GraphProps = {
    scale: graphScale,
    startDate: graphStartDate,
    endDate: graphEndDate
  }

  const getTooltip = (): string => {
    if (meter.deleted) {
      return t('settings_dialog.meter_deleted')
    } else if (isMeterOutdated) {
      return t('settings_dialog.meter_outdated')
    }

    return ''
  }

  const refreshMeterHistoryValues = (props: GraphProps = graphProps): void => {
    dispatch(
      FetchMeterHistoryValuesAction({
        id: meter.id,
        start: props.startDate.unix(),
        end: props.endDate.unix(),
        scale: props.scale
      }))
  }

  const onExpandMeter = (): void => {
    if (!isExpanded) {
      // Row expanded! Load the graph data
      refreshMeterHistoryValues()
    }

    setExpandedMeterId(meter.id)
    setIsExpanded(!isExpanded)
  }

  const onChangeGraphScale = (scale: MeterDataScale): void => {
    setGraphScale(scale)
    const calculatedGraphProps = calculateDateRangeByScale(scale)

    setGraphStartDate(calculatedGraphProps.startDate)
    setGraphEndDate(calculatedGraphProps.endDate)
    refreshMeterHistoryValues(calculatedGraphProps)
  }

  const onChangeGraphDates = (newGraphProps: GraphProps): void => {
    setGraphStartDate(newGraphProps.startDate)
    setGraphEndDate(newGraphProps.endDate)
    refreshMeterHistoryValues(newGraphProps)
  }

  const moveGraphDatesBack = (): void => {
    const calculatedGraphProps = calculateDateRangeShiftingToPrevUnit(graphStartDate, graphScale)
    onChangeGraphDates(calculatedGraphProps)
  }

  const moveGraphDatesForward = (): void => {
    const calculatedGraphProps = calculateDateRangeShiftingToNextUnit(graphStartDate, graphScale)
    onChangeGraphDates(calculatedGraphProps)
  }

  // Prevent multiple meters from being expanded at the same time
  if (isExpanded && expandedMeterId !== meter.id) {
    setIsExpanded(false)
  }

  return (
        <>
            <Tooltip style={{ fontSize: '12em' }} arrow title={getTooltip()}>
                <TableRow
                    key={meter.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(9,1fr)',
                      backgroundColor: isMeterOutdated || meter.deleted ? '#f7dfdf' : 'inherit',
                      textDecoration: meter.deleted ? 'line-through' : 'inherit'
                    }}
                >
                    <StyledTableCell>
                        <img src={logo} alt="lora-logo" style={{ width: '35px' }}/>
                        {isPulse && <PulseIndicator/>}
                    </StyledTableCell>
                    <StyledTableCell>
                        <StyledInput
                            isIdInput={true}
                            readOnly
                            value={meter.devEui ?? ''}
                            style={idVisibility}
                        />
                    </StyledTableCell>
                    <StyledTableCell>
                        <StyledInput
                            isIdInput={true}
                            readOnly
                            style={usageVisibility}
                            value={meter.location ?? ''}
                        />
                    </StyledTableCell>
                    <StyledTableCell>
                        <MetersTableLabelsField
                            meterLabels={meter.labels}
                            onLabelExpandClick={onLabelExpandClick}
                            labelVisibility={labelVisibility}
                        />
                    </StyledTableCell>
                    <StyledTableCell>
                        <AlarmStatusIcon
                            alarms={alarms}
                            alarmSettings={meter.alarmSettings}
                        />
                    </StyledTableCell>
                    <StyledTableCell>{updatedValue}</StyledTableCell>
                    <StyledTableCell>{dateAndTimeCoupling}</StyledTableCell>
                    <StyledTableCell>{dateAndTimeUpdating}</StyledTableCell>
                    <StyledTableCell
                        style={{ display: 'flex', justifyContent: 'space-evenly' }}
                    >
                        <GraphIconButton
                            onClick={onExpandMeter}
                            style={{ paddingTop: '6px' }}
                            data-testid="meter-expand-button"
                        >
                            {isExpanded ? <KeyboardArrowDown/> : <BarChart/>}
                        </GraphIconButton>
                        <SettingsIcon
                            onClick={onSettingsIconClick}
                            style={{ cursor: 'pointer', color: '#6e84a3' }}
                        />
                    </StyledTableCell>
                </TableRow>
            </Tooltip>
            {isExpanded && <ExpandedMeterRow meter={meter}
                                             alarms={alarms}
                                             graphProps={graphProps}
                                             onChangeGraphScale={onChangeGraphScale}
                                             onMoveGraphDatesBack={moveGraphDatesBack}
                                             onMoveGraphDatesForward={moveGraphDatesForward}
            />}
        </>
  )
}

/* Child-components */

interface ExpandedMeterRowProps {
  meter: Meter
  alarms: Alarm[]
  graphProps: GraphProps
  onChangeGraphScale: (scale: MeterDataScale) => void
  onMoveGraphDatesBack: () => void
  onMoveGraphDatesForward: () => void
}

function ExpandedMeterRow(props: Readonly<ExpandedMeterRowProps>): React.ReactElement {
  const { meter, alarms, graphProps, onChangeGraphScale, onMoveGraphDatesBack, onMoveGraphDatesForward } = props

  const { t } = useTranslation()
  const { classes } = useTabStyles()

  const fetchingHistoryGraphData = useAppSelector((state: RootState) => state.meter.history.fetchingHistoryGraphData)
  const historyGraphData = useAppSelector((state: RootState) => state.meter.history.historyGraphData)

  const [currentTab, setCurrentTab] = React.useState<number | boolean>(false)

  const [tabsExpanded, setTabsExpanded] = React.useState<boolean>(false)
  const [graphSourceType, setGraphSourceType] = React.useState<string>('water')

  const switchTab = (_: React.ChangeEvent<object>, newTab: number): void => {
    setCurrentTab(newTab)

    if (!tabsExpanded) {
      // Open tabs if they aren't already open (saves 1 click from user)
      setTabsExpanded(true)
    }
  }

  const getLabelChips = (): ReactElement[] | null => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!meter.labels) {
      return null
    }

    return meter.labels.map((i) => <StyledLabel label={i} key={i}/>)
  }

  return (
        <TableRow style={{ padding: '20px' }}>
            <StyledTableCell>
                <Grid container style={{ display: 'block' }}>
                    {/* BASIC INFO */}
                    <Grid item xs container spacing={1} direction="row">
                        <Grid item xs={3}>
                            <ColoredLabel>Sarjanumero</ColoredLabel>
                            <ColoredValue>{meter.transmitterSerialNumber}</ColoredValue>
                        </Grid>
                        <Grid item xs={3}>
                            <ColoredLabel>{t('serial_number')}</ColoredLabel>
                            <ColoredValue>{meter.serialNumber}</ColoredValue>
                        </Grid>
                        <Grid item xs={3}>
                            <ColoredLabel>{t('serial_number_generic')}</ColoredLabel>
                            {getLabelChips()}
                        </Grid>
                    </Grid>

                    {/* GRAPH DATA */}
                    <Grid
                        item
                        xs
                        spacing={1}
                        container
                        direction="row"
                        style={{ marginTop: '2em' }}
                    >
                        <Grid item xs={6} style={{ color: '#2B3B55' }}>
                <span style={{ marginRight: '1em', fontSize: '12px' }}>
                  {t('graph_dialog.usage_history')}
                </span>
                            <b>
                                {`${graphProps.startDate.format(
                                    'DD.MM.YYYY'
                                )} - ${graphProps.endDate.format('DD.MM.YYYY')}`}
                            </b>
                        </Grid>
                        <Grid item xs={2}>
                            <Select
                                className={classes.graphSelects}
                                value={graphSourceType}
                                onChange={(e) => {
                                  setGraphSourceType(e.target.value)
                                }}
                                variant="standard"
                            >
                                <MenuItem value={'water'} key={'water'}>
                                    <span style={{ margin: '0 1em 0 1em' }}>{t('graph_dialog.water_usage')}</span>
                                </MenuItem>
                                <MenuItem value={'temperature'} key={'temperature'}>
                                    <span style={{ margin: '0 1em 0 1em' }}>{t('temperature')}</span>
                                </MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={2}>
                            <Select
                                className={classes.graphSelects}
                                onChange={(e: SelectChangeEvent<MeterDataScale>) => {
                                  onChangeGraphScale(e.target.value as MeterDataScale)
                                }}
                                value={graphProps.scale}
                                variant="standard"
                            >
                                <MenuItem value={'single'} key={'single'}>
                    <span style={{ margin: '0 1em 0 1em' }}>
                      {t('graph_dialog.detailed_usage')}
                    </span>
                                </MenuItem>
                                <MenuItem value={'day'} key={'day'}>
                                            <span style={{ margin: '0 1em 0 1em' }}
                                                  data-testid='daily-usage'>{t('graph_dialog.daily_usage')}</span>
                                </MenuItem>
                                <MenuItem value={'week'} key={'week'}>
                                            <span
                                                style={{ margin: '0 1em 0 1em' }}>{t('graph_dialog.weekly_usage')}</span>
                                </MenuItem>
                                <MenuItem value={'month'} key={'month'}>
                    <span style={{ margin: '0 1em 0 1em' }}>
                      {t('graph_dialog.monthly_usage')}
                    </span>
                                </MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={2}>
                            <div style={{ float: 'right', height: '2em' }}>
                                <IconButton
                                    className={classes.graphControls}
                                    onClick={() => {
                                      onMoveGraphDatesBack()
                                    }}
                                    size="large"
                                >
                                    <KeyboardArrowLeft/>
                                </IconButton>
                                <Button
                                    style={{ height: '24px' }}
                                    variant="contained"
                                    className={classes.graphControls}
                                    disableElevation
                                    disableFocusRipple
                                    disableRipple
                                >
                                    {formatDisplayDate(graphProps)}
                                </Button>
                                <IconButton
                                    className={classes.graphControls}
                                    onClick={() => {
                                      onMoveGraphDatesForward()
                                    }}
                                    size="large"
                                >
                                    <KeyboardArrowRight/>
                                </IconButton>
                            </div>
                        </Grid>
                    </Grid>

                    {/* GRAPH ITSELF */}
                    <Grid
                        item
                        xs
                        spacing={1}
                        container
                        direction="row"
                        style={{ marginTop: '1em' }}
                    >
                        <Grid xs={12}>
                            {fetchingHistoryGraphData && (
                                <CircularProgress
                                    style={{ marginLeft: 'calc(50% - 20px)' }}
                                />
                            )}
                            {graphSourceType === 'water' &&
                                !fetchingHistoryGraphData && (
                                    <Bar
                                        data={historyGraphData.water}
                                        options={graphOptions}
                                    />
                            )}
                            {graphSourceType === 'temperature' &&
                                !fetchingHistoryGraphData && (
                                    <Line
                                        data={historyGraphData.temperature}
                                        options={graphOptions}
                                    />
                            )}
                        </Grid>
                    </Grid>

                    {/* TABS */}
                    <Grid
                        item
                        xs
                        spacing={1}
                        container
                        direction="row"
                        style={{ marginTop: '2em' }}
                    >
                        <Grid
                            item
                            xs
                            style={{
                              border: '1px #EDF2F9',
                              borderRadius: '4px 4px 0 0',
                              borderStyle: 'solid',
                              padding: '0'
                            }}
                        >
                            <StyledAppBar position="static">
                                <Tabs
                                    value={currentTab ?? false}
                                    className={classes.root}
                                    onChange={switchTab}
                                >
                                    <Tab
                                        disableRipple
                                        label={`${t('graph_dialog.alarms')}: ${
                                            alarms.filter((a) => a.active && a.supported).length
                                        }`}
                                        data-testid={'alarms-tab'}
                                    />
                                    <IconButton
                                        disableRipple
                                        className={classes.tabsLastElement}
                                        onClick={() => {
                                          setTabsExpanded(!tabsExpanded)
                                        }}
                                        size="large"
                                    >
                                        {tabsExpanded
                                          ? (
                                                <KeyboardArrowDown fontSize="large"/>
                                            )
                                          : (
                                                <KeyboardArrowRight fontSize="large"/>
                                            )}
                                    </IconButton>

                                </Tabs>
                            </StyledAppBar>
                            {tabsExpanded && <AlarmsTable alarms={alarms}/>}
                        </Grid>
                    </Grid>
                </Grid>
            </StyledTableCell>
        </TableRow>
  )
}

function AlarmsTable({ alarms }: { alarms: Alarm[] }): ReactElement {
  const { t } = useTranslation()

  return (<TableContainer>
        <Table>
            <TableHead>
                <TableRow>

                    <TableHeadCellBordered>{'#'}</TableHeadCellBordered>
                    <TableHeadCellBordered>
                        {t('alarm')}
                    </TableHeadCellBordered>
                    <TableHeadCellBordered>
                        {t('description')}
                    </TableHeadCellBordered>
                    <TableHeadCellBordered>
                        {t('state')}
                    </TableHeadCellBordered>
                </TableRow>
            </TableHead>
            <TableBody>
                {alarms.map((i: Alarm, index: number) => (
                    <AlarmsDetails
                        key={index}
                        alarm={i}
                        index={index}
                    />
                ))}
            </TableBody>
        </Table>
    </TableContainer>)
}

function PulseIndicator(): ReactElement {
  return (
        <div style={{ display: 'inline', color: '#0080FF', verticalAlign: 'middle' }}>
            &nbsp;Pulse
        </div>
  )
}

/* Helper Functions */

function calculateAlarms(meter: Meter): Alarm[] {
  const alarms: Alarm[] = []

  if (meter.latestLoraStatus !== null) {
    for (const key in meter.latestLoraStatus) {
      if (!isAlarmKey(key)) continue
      const alarmType = getAlarmTypeByKey(key)
      const supported = meterSupportsAlarm(
        meter.type,
        alarmType
      )
      alarms.push({
        type: alarmType,
        active: supported && meter.latestLoraStatus[key],
        supported
      })
    }
  }

  const flowLimitIsSupported = meterSupportsAlarm(
    DeviceType[meter.type],
    FLOW_LIMIT
  )

  alarms.push(
    {
      type: FLOW_LIMIT,
      active: flowLimitIsSupported && meter.flowLimitAlertActive,
      supported: flowLimitIsSupported
    }
  )

  return alarms
}

function calculateDateRangeByScale(scale: MeterDataScale): GraphProps {
  let startDate: moment.Moment = moment().locale('fi').startOf('day')
  let endDate: moment.Moment = startDate.clone().endOf('day')
  switch (scale) {
    case 'single':
      startDate = moment().locale('fi').startOf('day')
      endDate = startDate.clone().endOf('day')
      break
    case 'day':
      startDate = moment().locale('fi').startOf('month')
      endDate = startDate.clone().endOf('month')
      break
    case 'week':
      startDate = moment()
        .locale('fi')
        .subtract(3, 'weeks')
        .startOf('week')
      endDate = moment().endOf('week')
      break
    case 'month':
      startDate = moment().locale('fi').startOf('year')
      endDate = startDate.clone().endOf('year')
      break
  }

  return {
    startDate, endDate, scale
  }
}

function calculateDateRangeShiftingToNextUnit(graphStartDate: Moment, scale: MeterDataScale): GraphProps {
  let startDate: Moment
  let endDate: Moment
  switch (scale) {
    case 'single':
      startDate = graphStartDate.clone().add(1, 'days').startOf('day')
      endDate = startDate.clone().endOf('day')
      break
    case 'day':
      startDate = graphStartDate.clone().add(1, 'months').startOf('month')
      endDate = startDate.clone().endOf('month')
      break
    case 'week':
      startDate = graphStartDate.clone().add(4, 'weeks').startOf('week')
      endDate = startDate.clone().add(3, 'weeks').endOf('week')
      break
    case 'month':
      startDate = graphStartDate.clone().add(1, 'years').startOf('year')
      endDate = startDate.clone().endOf('year')
      break
    default:
      startDate = moment()
      endDate = moment()
  }

  return { startDate, endDate, scale }
}

function calculateDateRangeShiftingToPrevUnit(graphStartDate: Moment, scale: MeterDataScale): GraphProps {
  let startDate: moment.Moment
  let endDate: moment.Moment

  switch (scale) {
    case 'single':
      startDate = graphStartDate
        .clone()
        .subtract(1, 'days')
        .startOf('day')
      endDate = startDate.clone().endOf('day')
      break
    case 'day':
      startDate = graphStartDate
        .clone()
        .subtract(1, 'months')
        .startOf('month')
      endDate = startDate.clone().endOf('month')
      break
    case 'week':
      startDate = graphStartDate
        .clone()
        .subtract(4, 'weeks')
        .startOf('week')
      endDate = startDate.clone().add(3, 'weeks').endOf('week')
      break
    case 'month':
      startDate = graphStartDate
        .clone()
        .subtract(1, 'years')
        .startOf('year')
      endDate = startDate.clone().endOf('year')
      break
    default:
      startDate = moment()
      endDate = moment()
  }
  return { startDate, endDate, scale }
}

function formatDisplayDate(props: GraphProps): string | undefined {
  switch (props.scale) {
    case 'single':
      return props.endDate.format('DD/MM/YYYY')
    case 'day':
      return props.endDate.format('MM/YYYY')
    case 'week':
      return `${props.startDate.format('WW')}-${props.endDate.format('WW')}`
    case 'month':
      return props.endDate.format('YYYY')
    default:
      return undefined
  }
}
